import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { withTheme } from 'glamorous';
import { withRouter } from 'react-router-dom';
import { getEvent } from '../../../../../selectors/events';
import { FormSubHeader, actionContainer } from '../styles';
import Guest from './Guest';
import TooltipLink from './TooltipLink';

class GuestList extends React.Component {
  static propTypes = {
    isTable: PropTypes.bool,
    value: PropTypes.arrayOf(
      PropTypes.shape({
        firstName: PropTypes.string,
        lastName: PropTypes.string,
      })
    ),
    error: PropTypes.arrayOf(
      PropTypes.shape({
        firstName: PropTypes.string,
        lastName: PropTypes.string,
      })
    ),
    onChange: PropTypes.func,
    onBlur: PropTypes.func,
    event: PropTypes.object,
    theme: PropTypes.shape({
      color: PropTypes.string,
    }),
    match: PropTypes.shape({
      params: PropTypes.shape({
        eventId: PropTypes.string,
      }),
    }),
  };

  guestChange = index => name => (value, validation) => {
    const {
      value: valueList = [],
      error: errorList = [],
      onChange,
    } = this.props;

    // find the guest being edited in the value list by index
    const currentGuestValue = valueList[index];

    // access the field being edited by name
    const currentFieldValue = currentGuestValue[name];

    // handle any validation that has come back
    const { isValid = true, message = '', restrictInput } = validation || {};
    const newFieldValue = !isValid && restrictInput ? currentFieldValue : value;
    const newFieldError = isValid || restrictInput ? '' : message;

    // replace old entries in error/value lists
    const newValueList = valueList.map(
      (v, i) =>
        i === index ? { ...currentGuestValue, [name]: newFieldValue } : v
    );
    const newErrorList = errorList.map(
      (e, i) =>
        i === index ? { ...errorList[index], [name]: newFieldError } : e
    );

    // pass isValid as false so errors won't be overwritten with string
    return onChange(newValueList, {
      isValid: false,
      message: newErrorList,
    });
  };

  guestBlur = index => evt => {
    const { value: valueList = [], error: errorList = [], onBlur } = this.props;

    // get guest being blurred by index
    const currentGuestValue = valueList[index];
    let newGuestError = errorList[index];

    // if one has length and the other doesn't
    const guestValues = Object.values(currentGuestValue);
    if (
      guestValues.some(v => v.trim().length !== 0) &&
      guestValues.some(v => v.trim().length === 0)
    ) {
      newGuestError = Object.keys(newGuestError).reduce((acc, curr) => {
        // put required message on any empty fields
        if (!currentGuestValue[curr].trim().length) {
          return { ...acc, [curr]: 'This field is required.' };
        }
        return { ...acc, [curr]: newGuestError[curr] };
      }, {});
    }

    if (
      Object.values(errorList[index]).some(
        errMsg => errMsg === 'This field is required.'
      ) &&
      guestValues.every(v => v.trim().length === 0)
    ) {
      newGuestError = {
        firstName: '',
        lastName: '',
      };
    }

    // update error list with new error
    const newErrorList = errorList.map(
      (e, i) => (i === index ? newGuestError : e)
    );

    // call onBlur with isValid false so we don't overwrite error array with string
    return onBlur(evt, { isValid: false, message: newErrorList });
  };

  removeAtIndex = index => (acc, curr, i) => {
    if (i === index) {
      return acc;
    }
    return [...acc, curr];
  };

  deleteGuestClick = index => () => {
    const {
      value: valueList = [],
      error: errorList = [],
      onChange,
    } = this.props;

    const newValueList = valueList.reduce(this.removeAtIndex(index), []);
    const newErrorList = errorList.reduce(this.removeAtIndex(index), []);

    return onChange(newValueList, { isValid: false, message: newErrorList });
  };

  checkGuestsAreValid = () => {
    const { value: valueList = [] } = this.props;
    const lastGuest = valueList[valueList.length - 1];
    const { firstName = '', lastName = '' } = lastGuest || {};

    return !lastGuest || firstName.trim() !== '' || lastName.trim() !== '';
  };

  addGuest = () => {
    if (!this.checkGuestsAreValid()) {
      return;
    }

    const {
      value: valueList = [],
      error: errorList = [],
      onChange,
    } = this.props;

    const newValueList = [...valueList, { firstName: '', lastName: '' }];
    const newErrorList = [...errorList, { firstName: '', lastName: '' }];

    return onChange(newValueList, { isValid: false, message: newErrorList });
  };

  // if a guest limit was provided, take the lowest of: remaining available seats,
  // or remaining guests in limit
  // if there is no guest limit, the number of allowed guests is the remaining available seats
  canAddGuests = () => {
    const { event, value: guests = [], isTable } = this.props;
    const { numberOfSeatsAvailable, guestLimit } = event;
    const numCurrentGuests = guests.length;
    // take 1 for user, take number of visible guestInputs
    const availableSeats = numberOfSeatsAvailable - numCurrentGuests - 1;
    const numberOfAllowedGuests =
      guestLimit
        ? Math.min(availableSeats, guestLimit - numCurrentGuests)
        : availableSeats;

    return !isTable && numberOfAllowedGuests > 0;
  };

  render() {
    const {
      isTable,
      value: valueList,
      error: errorList,
      event: { numberOfSeatsAvailable, guestLimit } = {},
    } = this.props;

    const canHasGuests =
      numberOfSeatsAvailable > 1 && (!guestLimit || guestLimit > 0);

    if (!canHasGuests && !isTable) {
      return null;
    }

    return (
      <div>
        <FormSubHeader>
          {isTable ? 'Enter Guest Names' : 'Add Guests'}
        </FormSubHeader>
        {isTable ? (
          <p
            style={{
              color: '#585858',
              marginBottom: '10px',
              marginTop: '10px',
            }}
          >
            {'Please add your table guest names. ' +
              'If you do not currently have your table guest names, ' +
              'please call your state office with them 3 days prior ' +
              'to the event to allow for name tag creation.'}
          </p>
        ) : null}
        {valueList.map((g, i) => {
          return (
            <Guest
              key={i.toString()}
              value={g}
              guestNumber={i + 1}
              error={errorList[i]}
              deleteGuestClick={isTable ? null : this.deleteGuestClick(i)}
              onBlur={this.guestBlur(i)}
              onChange={this.guestChange(i)}
              disabled={isTable && i === 0}
            />
          );
        })}
        {this.canAddGuests() ? (
          <div style={actionContainer}>
            <TooltipLink
              label="Add a guest"
              tooltip="Please enter information for guests"
              icon="add"
              showTooltip={!this.checkGuestsAreValid()}
              onClick={this.addGuest}
              style={{
                marginTop: '14px',
                marginBottom: '20px',
                color: '#00AD50',
                fontSize: '15px',
                fontWeight: 'bold',
              }}
            />
          </div>
        ) : null}
      </div>
    );
  }
}

const mapStateToProps = (state, ownProps) => {
  const { match: { params: { eventId: id = '' } = {} } = {} } = ownProps;
  return {
    event: getEvent(state, { id }),
  };
};

export default withRouter(connect(mapStateToProps)(withTheme(GuestList)));



// WEBPACK FOOTER //
// ./src/App/events/Views/Detail/RegistrationCard/RegistrationForm/GuestList/index.js